import { defineStore } from 'pinia'
import notificationsService, { EMPTY_UNREAD } from '@/services/notifications.service'
import type { NotificationFilters, UnreadState } from '@/services/notifications.service'
import { isAbortError } from '@/api'
import type { Notification, NotificationKind } from '@/entities/notification'

/** The bell popup shows this many of the newest, whatever the page filters. */
const RECENT = 8
const PAGE_SIZE = 25

/** The page's tabs: everything, unread only, or one kind. */
export type NotificationTab = 'all' | 'unread' | NotificationKind

export function tabFilters(tab: NotificationTab): NotificationFilters {
  if (tab === 'all') return {}
  if (tab === 'unread') return { unread: true }
  return { kind: tab }
}

/**
 * The bell (W11).
 *
 *   pages → stores → services → api
 *
 * Two lists over one table: `recent` for the topbar popup (the newest few,
 * unfiltered) and `items` for the notifications page (filtered, paged). Read
 * state is changed optimistically in both and rolled back if the API refuses.
 *
 * The unread count arrives three ways — a fetch, the live stream, and this
 * tab's own clicks — and `unread` is the one place it is kept. When the stream
 * reports a newer notification than the ones on screen, the lists are
 * re-read rather than patched: the server renders the words, so only the
 * server can supply the new row.
 */
interface Snapshot {
  recent: Notification[]
  items: Notification[]
  total: number
  unread: UnreadState
}

export interface NotificationsState {
  recent: Notification[]
  recentLoaded: boolean
  items: Notification[]
  total: number
  tab: NotificationTab
  limit: number
  offset: number
  pageLoaded: boolean
  unread: UnreadState
  loading: boolean
  error: unknown
  _abortController: AbortController | null
  _stream: AbortController | null
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    recent: [],
    recentLoaded: false,
    items: [],
    total: 0,
    tab: 'all',
    limit: PAGE_SIZE,
    offset: 0,
    pageLoaded: false,
    unread: { ...EMPTY_UNREAD },
    loading: false,
    error: null,
    _abortController: null,
    _stream: null,
  }),

  getters: {
    unreadCount: (state) => state.unread.total,
    page: (state) => Math.floor(state.offset / state.limit) + 1,
    pageCount: (state) => Math.max(1, Math.ceil(state.total / state.limit)),
    hasPrev: (state) => state.offset > 0,
    hasNext: (state) => state.offset + state.limit < state.total,
    streaming: (state) => state._stream != null,
  },

  actions: {
    async fetchUnread() {
      try {
        this.unread = await notificationsService.unread()
      } catch (err) {
        if (!isAbortError(err)) this.error = err
      }
    },

    async fetchRecent() {
      try {
        const page = await notificationsService.list({ limit: RECENT })
        this.recent = page.items
        this.recentLoaded = true
      } catch (err) {
        if (!isAbortError(err)) this.error = err
      }
    },

    async fetchPage() {
      this._abortController?.abort('superseded')
      const controller = new AbortController()
      this._abortController = controller
      this.loading = true
      this.error = null
      try {
        const page = await notificationsService.list(
          { ...tabFilters(this.tab), limit: this.limit, offset: this.offset },
          { signal: controller.signal }
        )
        if (this._abortController !== controller) return
        this.items = page.items
        this.total = page.total
        this.pageLoaded = true
      } catch (err) {
        if (isAbortError(err)) return
        this.error = err
      } finally {
        if (this._abortController === controller) {
          this.loading = false
          this._abortController = null
        }
      }
    },

    /** A tab change is a filter change: back to the first page. */
    async setTab(tab: NotificationTab) {
      this.tab = tab
      this.offset = 0
      await this.fetchPage()
    },

    async goToPage(page: number) {
      const target = Math.min(Math.max(1, page), this.pageCount)
      this.offset = (target - 1) * this.limit
      await this.fetchPage()
    },

    /** Mark one read, everywhere it is shown. */
    async markRead(id: number) {
      const row = [...this.recent, ...this.items].find((n) => n.id === id)
      if (!row?.unread) return
      const before = this.snapshot()
      const now = new Date().toISOString()
      const flip = (n: Notification) => (n.id === id ? { ...n, unread: false, readAt: now } : n)
      this.recent = this.recent.map(flip)
      if (this.tab === 'unread') {
        this.items = this.items.filter((n) => n.id !== id)
        this.total = Math.max(0, this.total - 1)
      } else {
        this.items = this.items.map(flip)
      }
      this.countDown(row.kind, 1)
      try {
        await notificationsService.markRead(id)
      } catch (err) {
        this.restore(before)
        throw err
      }
    },

    async markAllRead() {
      const before = this.snapshot()
      const now = new Date().toISOString()
      const clear = (n: Notification) => (n.unread ? { ...n, unread: false, readAt: now } : n)
      this.recent = this.recent.map(clear)
      if (this.tab === 'unread') {
        this.items = []
        this.total = 0
      } else {
        this.items = this.items.map(clear)
      }
      this.unread = { ...this.unread, total: 0, byKind: {} }
      try {
        await notificationsService.markAllRead()
      } catch (err) {
        this.restore(before)
        throw err
      }
    },

    async remove(id: number) {
      const row = [...this.recent, ...this.items].find((n) => n.id === id)
      if (!row) return
      const before = this.snapshot()
      this.recent = this.recent.filter((n) => n.id !== id)
      if (this.items.some((n) => n.id === id)) {
        this.items = this.items.filter((n) => n.id !== id)
        this.total = Math.max(0, this.total - 1)
      }
      if (row.unread) this.countDown(row.kind, 1)
      try {
        await notificationsService.remove(id)
      } catch (err) {
        this.restore(before)
        throw err
      }
      /* The page may have been emptied by the removal; step back rather than
       * show "no notifications" on page 3 of 3. */
      if (this.pageLoaded && !this.items.length && this.offset > 0) {
        await this.goToPage(this.page - 1)
      }
    },

    /**
     * A new unread state from the stream (or a fetch). Re-reads the lists
     * when something arrived that they do not show yet, or when the count
     * moved for a reason this tab did not cause (another tab, another device).
     */
    applyUnread(next: UnreadState) {
      const prev = this.unread
      this.unread = next
      const arrived =
        next.latestId != null && (prev.latestId == null || next.latestId > prev.latestId)
      const moved = next.total !== prev.total
      const known = prev.latestId != null || prev.total > 0
      if (!known) return
      if ((arrived || moved) && this.recentLoaded) void this.fetchRecent()
      if (arrived && this.pageLoaded && this.offset === 0) void this.fetchPage()
    },

    /**
     * Follow the live count until sign-out. Idempotent: the layout calls it on
     * every mount. Nothing is thrown out of it — a stream that cannot connect
     * retries with backoff inside the service.
     */
    startStream() {
      if (this._stream) return
      const controller = new AbortController()
      this._stream = controller
      void notificationsService
        .follow((state) => this.applyUnread(state), { signal: controller.signal })
        .catch(() => {})
        .finally(() => {
          if (this._stream === controller) this._stream = null
        })
    },

    stopStream() {
      this._stream?.abort('stopped')
      this._stream = null
    },

    /** Everything back to empty — on sign-out, so the next person starts clean. */
    reset() {
      this.stopStream()
      this._abortController?.abort('reset')
      this.$reset()
    },

    /* ------------------------------------------------------------ internals */

    countDown(kind: string, n: number) {
      const byKind = { ...this.unread.byKind } as Record<string, number>
      if (byKind[kind]) byKind[kind] = Math.max(0, byKind[kind] - n)
      this.unread = {
        ...this.unread,
        total: Math.max(0, this.unread.total - n),
        byKind,
      }
    },

    snapshot(): Snapshot {
      return {
        recent: this.recent,
        items: this.items,
        total: this.total,
        unread: this.unread,
      }
    },

    restore(s: Snapshot) {
      this.recent = s.recent
      this.items = s.items
      this.total = s.total
      this.unread = s.unread
    },
  },
})
